import {
  useCustomerOrdersV2,
  isOrderAwaitingPayment,
} from "@/api/use-customer-orders-v2";
import { PayOrderButton } from "./pay-order-button";
import { type Order, ShipmentStatus } from "@/api.types.v2";
import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CreateOrderButton } from "./create-order-button";

const IN_MOTION = new Set<ShipmentStatus>([
  ShipmentStatus.LABEL_READY,
  ShipmentStatus.DROPPED_AT_CARRIER,
  ShipmentStatus.PICKED_UP,
  ShipmentStatus.IN_TRANSIT,
  ShipmentStatus.AT_FACILITY,
  ShipmentStatus.OUT_FOR_DELIVERY,
]);

function spellOut(n: number, words: string[]): string {
  if (n >= 0 && n < words.length) return words[n];
  return String(n);
}

function formatDateEyebrow(d: Date, locale: string): string {
  const wk = d.toLocaleDateString(locale, { weekday: "short" });
  const md = d.toLocaleDateString(locale, { month: "short", day: "numeric" });
  return `${wk} · ${md}`;
}

function formatCurrencyCents(cents?: number | null): string {
  if (cents == null) return "$0.00";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

export function DashboardMobileHero() {
  const { t, i18n } = useTranslation("app");
  const { data: ordersResponse } = useCustomerOrdersV2({ page: 1, limit: 200 });
  const orders = ordersResponse?.data ?? [];

  let inMotion = 0;
  let unpaid = 0;
  for (const o of orders) {
    const s = o.shipments?.[0];
    if (!s || s.cancellationRequestedAt) continue;
    if (s.status && IN_MOTION.has(s.status)) inMotion++;
    if (isOrderAwaitingPayment(o)) unpaid++;
  }

  // Pick the first pending-payment order to surface as a banner.
  // Design section 6 dashboard frame shows "Payment needed · EZX-XXXXX /
  // Pay $X by Y / [Pay]" — most-urgent call-to-action above the list.
  const urgentOrder = orders.find(isOrderAwaitingPayment);
  const urgentShipment = urgentOrder?.shipments?.[0];
  const numberWords = t("dashboardMobile.numberWords", {
    returnObjects: true,
  }) as string[];

  const headline =
    inMotion === 0
      ? t("dashboardMobile.nothingInMotion")
      : inMotion === 1
        ? t("dashboardMobile.oneInMotion")
        : t("dashboardMobile.manyInMotion", {
            number: spellOut(inMotion, numberWords),
          });

  return (
    <div className="md:hidden -mx-4 px-4 pb-6 border-b border-default-200">
      <div className="text-tiny font-semibold uppercase tracking-wider text-default-500">
        {formatDateEyebrow(new Date(), i18n.resolvedLanguage ?? i18n.language)}
      </div>
      <h1 className="mt-2 text-[2.25rem] leading-[1.05] font-bold tracking-[-0.025em] text-default-900">
        {headline}
      </h1>
      {(inMotion > 0 || unpaid > 0) && (
        <div className="mt-4 flex items-baseline gap-5 text-sm">
          {inMotion > 0 && (
            <div>
              <span className="text-primary font-semibold">{inMotion}</span>{" "}
              <span className="text-default-500">
                {t("dashboardMobile.active")}
              </span>
            </div>
          )}
          {unpaid > 0 && (
            <div>
              <span className="text-danger-700 font-semibold">{unpaid}</span>{" "}
              <span className="text-default-500">
                {unpaid === 1
                  ? t("dashboardMobile.needsPayment")
                  : t("dashboardMobile.needPayment")}
              </span>
            </div>
          )}
        </div>
      )}
      <div className="mt-5">
        <CreateOrderButton mobile />
      </div>

      {urgentOrder && urgentShipment && (
        <UrgentPaymentBanner order={urgentOrder} />
      )}
    </div>
  );
}

function UrgentPaymentBanner({ order }: { order: Order }) {
  const { t } = useTranslation("app");
  const shipmentNumber = order.shipments?.[0]?.shipmentNumber ?? "";
  const totalCents = order.balanceCents ?? order.totalCents;
  return (
    <div className="mt-5 -mx-4 px-4 py-3.5 bg-danger-50 border-y border-danger-200 flex items-center gap-3">
      <AlertCircle className="h-5 w-5 text-danger-600 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-tiny font-semibold uppercase tracking-wider text-danger-700">
          {t("dashboardMobile.paymentNeeded")} · SHP-{shipmentNumber}
        </div>
        <div className="text-sm text-danger-700/85 mt-0.5">
          {t("dashboardMobile.payToReleaseLabel", {
            amount: formatCurrencyCents(totalCents),
          })}
        </div>
      </div>
      <PayOrderButton order={order} color="danger" />
    </div>
  );
}
